import React from "react";
import dayjs, { Dayjs } from "dayjs";
import "./CalendarSidebar.css";
import "dayjs/locale/ru";
dayjs.locale("ru");

export interface TaskFragment {
  color: string;
  done: boolean;
}
export type PrayerMap = Record<string, number>;
export type RingMap = Record<string, TaskFragment[]>;

interface Props {
  month: Dayjs;
  selected: Dayjs;
  onSelect: (d: Dayjs) => void;
  onMonthChange: (m: Dayjs) => void;
  prayerDone: PrayerMap;
  taskFrags: RingMap;
}

const iso = (d: Dayjs) => d.format("YYYY-MM-DD");
const isSame = (a: Dayjs, b: Dayjs) => iso(a) === iso(b);
const WEEK = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

/* Сегменты задач в виде кольца */
function buildRingGradient(frags: TaskFragment[]): string {
  if (!frags.length) return "transparent";
  const gap = 1;
  const step = 360 / frags.length;
  const stops: string[] = [];

  frags.forEach((f, idx) => {
    const start = idx * step + gap / 2;
    const end = (idx + 1) * step - gap / 2;
    const col = f.done ? f.color : `${f.color}99`; // прозрачность 60%
    stops.push(`${col} ${start}deg ${end}deg`);
  });

  return `conic-gradient(${stops.join(",")})`;
}

const CalendarSidebar: React.FC<Props> = ({
  month,
  selected,
  onSelect,
  onMonthChange,
  prayerDone,
  taskFrags,
}) => {
  const begin = month.startOf("month");
  const daysInMonth = month.daysInMonth();
  const days = Array.from({ length: daysInMonth }, (_, i) => begin.add(i, "day"));

  const paddingStart = begin.day() === 0 ? 6 : begin.day() - 1;
  const padded = [...Array(paddingStart).fill(null), ...days];

  return (
    <aside className="cal-sidebar">
      <h3 className="cal-month">
        <button className="month-btn" onClick={() => onMonthChange(month.subtract(1, "month"))}>
          ‹
        </button>
        <span>{month.format("MMMM YYYY")}</span>
        <button className="month-btn" onClick={() => onMonthChange(month.add(1, "month"))}>
          ›
        </button>
      </h3>

      <div className="cal-grid cal-head">
        {WEEK.map((w) => (
          <span key={w}>{w}</span>
        ))}
      </div>

      <div className="cal-grid">
        {padded.map((d, idx) =>
          d ? (
            <DayCell
              key={idx}
              day={d}
              isToday={isSame(d, dayjs())}
              isSelected={isSame(d, selected)}
              prayersDone={prayerDone[iso(d)] ?? 0}
              fragments={taskFrags[iso(d)] ?? []}
              onClick={() => onSelect(d)}
            />
          ) : (
            <span key={idx} />
          )
        )}
      </div>
    </aside>
  );
};

export default CalendarSidebar;

/* ───── Ячейка дня ───── */
interface CellProps {
  day: Dayjs;
  isToday: boolean;
  isSelected: boolean;
  prayersDone: number;
  fragments: TaskFragment[];
  onClick: () => void;
}

const DayCell: React.FC<CellProps> = ({
  day,
  isToday,
  isSelected,
  prayersDone,
  fragments,
  onClick,
}) => {
  const prayerDeg = ((prayersDone / 5) * 360).toFixed(1);
  const prayerBg =
    prayersDone > 0
      ? `conic-gradient(#4caf50 ${prayerDeg}deg, transparent 0deg)`
      : "transparent";

  const taskBg = buildRingGradient(fragments);

  return (
    <button
      className={`day-cell${isSelected ? " sel" : ""}${isToday ? " today" : ""}`}
      onClick={onClick}
      style={
        {
          "--prayer-ring": prayerBg,
          "--task-ring": taskBg,
        } as React.CSSProperties
      }
    >
      {day.date()}
    </button>
  );
};
